import { useState } from "react";
import { useParams } from "react-router-dom";
import { useFetch } from "../lib/useFetch";
import api from "../lib/api";
import { PageHeader, Panel, Stat, Loading } from "../components/common/ui";
import { SubTabs } from "../components/common/SubTabs";
import { AreaTrend, Bars } from "../components/common/Charts";
import { inr, sevColor } from "../lib/format";
import { cn } from "../lib/utils";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Sparkles } from "lucide-react";

const TABS = [
  { key: "overview", label: "Hub Overview" }, { key: "performance", label: "Performance" },
  { key: "capacity", label: "Capacity" }, { key: "productivity", label: "Productivity" },
];

export default function HubIntelligence() {
  const { tab = "overview" } = useParams();
  const { data: hubs, loading } = useFetch("/hubs", []);
  const [active, setActive] = useState(null);

  return (
    <div data-testid="hub-page">
      <PageHeader title="Hub Intelligence" subtitle="Multi-hub control center — rank, compare and drill into any retail hub." />
      <SubTabs base="/hubs" tab={tab} items={TABS} />
      {loading || !hubs ? <Loading rows={5} /> : (
        <>
          {tab === "overview" && <Leaderboard hubs={hubs} onOpen={setActive} />}
          {tab === "performance" && (
            <div className="grid lg:grid-cols-2 gap-6">
              <Panel title="Revenue by Hub (30d)"><Bars data={hubs.map((h) => ({ name: h.name, value: h.revenue }))} horizontal currency height={380} /></Panel>
              <Panel title="Growth by Hub"><Bars data={[...hubs].sort((a, b) => b.growth - a.growth).map((h) => ({ name: h.name, value: h.growth }))} horizontal height={380} /></Panel>
            </div>
          )}
          {tab === "capacity" && (
            <Panel title="Capacity Utilisation by Hub" subtitle="Hubs above 88% are at risk of overload">
              <Bars data={[...hubs].sort((a, b) => b.capacity - a.capacity).map((h) => ({ name: h.name, value: h.capacity }))} horizontal height={420} />
            </Panel>
          )}
          {tab === "productivity" && (
            <div className="grid lg:grid-cols-2 gap-6">
              <Panel title="Employee Productivity"><Bars data={[...hubs].sort((a, b) => b.productivity - a.productivity).map((h) => ({ name: h.name, value: h.productivity }))} horizontal height={380} /></Panel>
              <Panel title="Customer Retention %"><Bars data={[...hubs].sort((a, b) => b.retention - a.retention).map((h) => ({ name: h.name, value: h.retention }))} horizontal height={380} /></Panel>
            </div>
          )}
        </>
      )}
      <Hub360 hubId={active} onClose={() => setActive(null)} />
    </div>
  );
}

function Leaderboard({ hubs, onOpen }) {
  return (
    <Panel title="Hub Leaderboard" subtitle="Click a hub for full Hub 360 analysis">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Hub</TableHead><TableHead className="text-right">Revenue</TableHead>
            <TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Growth</TableHead>
            <TableHead className="text-right">SLA</TableHead><TableHead className="text-right">Complaints</TableHead>
            <TableHead className="text-right">Capacity</TableHead><TableHead className="text-right">Rating</TableHead>
            <TableHead className="text-right">Retention</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {hubs.map((h, i) => (
            <TableRow key={h.id} onClick={() => onOpen(h.id)} className="cursor-pointer hover:bg-slate-50" data-testid={`hub-row-${h.id}`}>
              <TableCell className="font-medium"><span className="text-slate-300 kpi-num mr-2">{i + 1}</span>{h.name}<span className="text-xs text-slate-400 ml-2">{h.zone}</span></TableCell>
              <TableCell className="text-right">₹{inr(h.revenue)}</TableCell>
              <TableCell className="text-right">{h.orders.toLocaleString("en-IN")}</TableCell>
              <TableCell className={cn("text-right font-medium", h.growth >= 0 ? "text-emerald-600" : "text-red-600")}>{h.growth}%</TableCell>
              <TableCell className={cn("text-right", h.sla < 95 ? "text-red-600 font-medium" : "")}>{h.sla}%</TableCell>
              <TableCell className="text-right">{h.complaints}</TableCell>
              <TableCell className={cn("text-right", h.capacity > 88 ? "text-amber-600 font-medium" : "")}>{h.capacity}%</TableCell>
              <TableCell className="text-right">{h.rating}</TableCell>
              <TableCell className="text-right">{h.retention}%</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}

function Hub360({ hubId, onClose }) {
  const { data } = useFetch(hubId ? `/hubs/${hubId}` : null, [hubId]);
  return (
    <Sheet open={!!hubId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto scrollbar-thin" data-testid="hub360-drawer">
        {!data ? <Loading rows={4} /> : (
          <>
            <SheetHeader>
              <SheetTitle className="font-heading">{data.hub.name} · Hub 360</SheetTitle>
            </SheetHeader>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <Stat label="Revenue" value={"₹" + inr(data.hub.revenue)} />
              <Stat label="Orders" value={data.hub.orders.toLocaleString("en-IN")} />
              <Stat label="AOV" value={"₹" + inr(data.hub.aov)} />
              <Stat label="Customers" value={data.customers} />
              <Stat label="Capacity" value={`${data.hub.capacity}%`} />
              <Stat label="SLA" value={`${data.hub.sla}%`} tone={data.hub.sla < 95 ? "text-red-600" : ""} />
              <Stat label="Productivity" value={data.hub.productivity} />
              <Stat label="Retention" value={`${data.hub.retention}%`} />
              <Stat label="AI Usage" value={`${data.hub.ai_usage}%`} />
            </div>
            <div className="mt-5 bg-secondary/5 border-l-2 border-secondary rounded p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-primary mb-1.5">
                <Sparkles className="w-4 h-4" /> Why is this hub performing this way?
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">{data.ai_analysis}</p>
            </div>
            <div className="mt-5">
              <Panel title="Revenue Trend (30d)"><AreaTrend data={data.rev_trend} currency height={200} /></Panel>
            </div>
            <div className="mt-5">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Recent Complaints</h4>
              <div className="space-y-2">
                {data.complaints_list.map((c) => {
                  const s = sevColor(c.severity === "High" ? "critical" : c.severity === "Medium" ? "warning" : "info");
                  return (
                    <div key={c.id} className="flex items-center gap-2 text-xs border border-slate-100 rounded-md p-2">
                      <span className={cn("w-1.5 h-1.5 rounded-full", s.dot)} />
                      <span className="font-medium text-slate-700">{c.category}</span>
                      <span className="text-slate-400">{c.date}</span>
                      <span className="ml-auto text-slate-400">{c.status}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
